/**
 * Component: TableOfContentsDrawer
 * SpecReferenceID: BROWSE-V1
 * Requirements: BROWSE-V1-R06
 *
 * Description: Auto-generated table-of-contents drawer for the
 *              BROWSE-V1 viewer. Takes the narrowed
 *              `sectionContent[]` array and renders one entry per
 *              section with `sectionHierarchy <= 2`. Deeper levels
 *              (3-5) are still rendered in the body by R05 but
 *              intentionally omitted from the TOC for usability —
 *              ~30 guidelines with 5-level nesting produces an
 *              unnavigable TOC otherwise.
 *
 *              The DOM contract for each entry is
 *              `id="toc_<sectionId>"`, mirroring the indico-ai-portal
 *              pattern so any shared highlight / selection code is
 *              structurally portable.
 *
 *              Clicking an entry invokes `onNavigate(sectionId)`,
 *              which the parent routes through the same
 *              `setSearchParams({sectionId})` flow the R22
 *              `useScrollToSection` hook already consumes. This
 *              keeps a single scroll implementation in the viewer
 *              — the TOC just drives the URL.
 *
 * Risk Links: R7
 * FMEA Links: FM7
 */

import React, { useMemo, useState } from 'react';
import { List } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import type { SectionContentEntry } from '../types';

interface TableOfContentsDrawerProps {
  sections: SectionContentEntry[];
  /**
   * Section id of the entry that should show as `aria-current`. The
   * parent tracks this via the same `?sectionId=` query param the
   * R22 hook consumes, so the TOC and the scroll-to-section state
   * are always consistent.
   */
  currentSectionId: number | null;
  /**
   * Fires when the user selects a TOC entry. The parent is
   * responsible for updating `?sectionId=` so `useScrollToSection`
   * picks up the new target.
   */
  onNavigate: (sectionId: number) => void;
}

export function TableOfContentsDrawer({
  sections,
  currentSectionId,
  onNavigate,
}: TableOfContentsDrawerProps): React.ReactElement {
  const [open, setOpen] = useState(false);

  // BROWSE-V1-R06: Top 2 levels only. `useMemo` keeps the narrowed
  // identity stable across drawer open/close so the Radix Sheet
  // doesn't re-run its enter animation for identical children.
  const tocEntries = useMemo(
    () => sections.filter((section) => section.sectionHierarchy <= 2),
    [sections],
  );

  const handleSelect = (sectionId: number): void => {
    setOpen(false);
    onNavigate(sectionId);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-label="Open table of contents"
          data-testid="toc-trigger"
        >
          <List className="mr-2 h-4 w-4" aria-hidden="true" />
          Contents
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-[320px] sm:w-[380px] overflow-y-auto"
        data-testid="toc-drawer"
      >
        <SheetHeader>
          <SheetTitle>Table of contents</SheetTitle>
        </SheetHeader>
        {tocEntries.length === 0 ? (
          <p
            className="mt-6 text-sm text-muted-foreground"
            data-testid="toc-empty"
          >
            No top-level sections to list for this guideline.
          </p>
        ) : (
          <nav
            aria-label="Guideline table of contents"
            className="mt-6"
            data-testid="toc-nav"
          >
            <ul className="flex flex-col gap-1">
              {tocEntries.map((entry) => {
                const isActive = entry.sectionId === currentSectionId;
                const indentClass =
                  entry.sectionHierarchy === 2 ? 'pl-6 text-sm' : 'pl-2 text-base font-medium';
                return (
                  <li key={entry.sectionId}>
                    <SheetClose asChild>
                      <button
                        id={`toc_${entry.sectionId}`}
                        type="button"
                        onClick={() => handleSelect(entry.sectionId)}
                        aria-current={isActive ? 'location' : undefined}
                        data-testid={`toc-entry-${entry.sectionId}`}
                        className={[
                          'w-full rounded-md py-2 text-left transition-colors',
                          'hover:bg-accent hover:text-accent-foreground',
                          'focus:outline-none focus:ring-2 focus:ring-primary',
                          isActive ? 'bg-accent text-accent-foreground' : 'text-foreground',
                          indentClass,
                        ].join(' ')}
                      >
                        {entry.sectionName}
                      </button>
                    </SheetClose>
                  </li>
                );
              })}
            </ul>
          </nav>
        )}
      </SheetContent>
    </Sheet>
  );
}
